import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { compact, flatten, isEmpty, uniqBy, values } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import SignedStatus from 'models/enums/SignedStatus';
import VertaforeStatuses from 'models/enums/VertaforeDocumentStatuses';
import VehicleModel from 'models/productManagement/vehicleModel/Model.VehicleModel';
import { useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { useSalesContractProvider } from '../Sales.Contract.Hooks';

export default function SalesContractFilters() {
  const { lang } = getLocales();
  const { user, permissions } = useAuthStore();
  const { listApi, statuses, pageRouter, canView } = useSalesContractProvider();
  const [signedStatus, setSignedStatus] = useState<Model.Enum.SignedStatus>(undefined);

  const dealersApi = useApi({
    action: Dealer.list,
    wait: true,
    body: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
  });

  const userListApi = useApi({
    action: User.list,
    body: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: !canView || !permissions.CREATE_CONTRACT,
  });

  const modelsApi = useApi({
    action: VehicleModel.list,
    wait: true,
  });

  return (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      filters={listApi.body}
      inline={isDesktop}
      onFilter={(newFilters) =>
        pageRouter.redirect(
          { ...pageRouter.params, action: undefined },
          {
            ...pageRouter.query,
            list: {
              ...newFilters,
              offset: 0,
            },
          }
        )
      }
      onReset={() => {
        pageRouter.redirect(
          { ...pageRouter.params, action: undefined },
          {
            ...pageRouter.query,
            list: undefined,
          }
        );
        setSignedStatus(undefined);
      }}
      onOpen={() => {
        if (!dealersApi.payload) dealersApi.execute((body) => body);
        if (!modelsApi.payload) modelsApi.execute((body) => body);
      }}
      render={({ tempFilters, setFilters }) => {
        const dealers = dealersApi?.payload?.data ?? [];

        const users = userListApi?.payload?.data ?? [];

        const products = !isEmpty(tempFilters?.dealerId)
          ? flatten(dealers.filter((d) => tempFilters?.dealerId?.some((id) => id === d.id)).map((d) => d.products))
          : uniqBy(flatten(dealers.map((d) => d.products)), (p) => p.id);

        const makes =
          compact(
            uniqBy(
              modelsApi?.payload?.data?.map((m) => m.vehicleMake),
              (m) => m.id
            )
          ) ?? [];

        const models =
          modelsApi?.payload?.data?.filter((m) =>
            tempFilters?.makeCode?.some((code) => code === m.vehicleMake?.makeCode)
          ) ?? [];

        return (
          <>
            <TextInput
              name="contractNumber"
              label={lang.contractNumber}
              value={tempFilters?.contractNumber}
              onChange={({ contractNumber }) => setFilters({ ...tempFilters, contractNumber })}
              icon={<Icon class="if-quill-pen" />}
            />
            <SelectInput
              name="createdById"
              label={lang.createdBy}
              data={users?.map((user) => ({
                id: user.id,
                title: user.fullName,
                icon: <Icon class="fas-user" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.createdById}
              onChange={({ createdById }) => setFilters({ ...tempFilters, createdById })}
              icon={<Icon class="fas-user" />}
            />
            <SelectInput
              name="statusCode"
              label={lang.status}
              data={statuses.map((item) => ({
                id: item.id,
                title: item.title,
                icon: <Icon class="fas-check" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.statusCode}
              onChange={({ statusCode }) => setFilters({ ...tempFilters, statusCode })}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="vertaforeStatus"
              label={lang.vertaforeStatus}
              data={Object.values(VertaforeStatuses).map((it) => ({
                id: it,
                title: it,
                icon: <Icon class="fas-double-check" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.vertaforeStatus}
              onChange={({ vertaforeStatus }) => setFilters({ ...tempFilters, vertaforeStatus })}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="signedStatus"
              label={lang.docusignStatus}
              data={values(SignedStatus).map((it) => ({
                id: it,
                title: it,
                icon: <Icon class="fas-double-check" />,
              }))}
              value={signedStatus}
              onChange={({ signedStatus }) => {
                const statusMap = {
                  'Signed manually': 'isSignedManually',
                  'Signed with DocuSign': 'isSignedWithDocuSign',
                  'Sent for signing': 'isSentForSigning',
                  'All signed': 'isSigned',
                };

                const updatedFilters = { ...tempFilters };

                values(statusMap).forEach((statusKey) => {
                  delete updatedFilters[statusKey];
                });

                if (signedStatus && statusMap[signedStatus]) {
                  updatedFilters[statusMap[signedStatus]] = true;
                }

                setFilters(updatedFilters);
                setSignedStatus(signedStatus);
              }}
              icon={<Icon class="fas-cloud" />}
            />
            <TextInput
              name="vin"
              label={lang.vin}
              value={tempFilters?.vin}
              onChange={({ vin }) => setFilters({ ...tempFilters, vin })}
              icon={<Icon class="if-quill-pen" />}
            />
            <NumberInput
              name="vehicleYear"
              label={lang.vehicleYear}
              value={tempFilters?.vehicleYear}
              onChange={({ vehicleYear }) => setFilters({ ...tempFilters, vehicleYear })}
              icon={<Icon class="if-quill-pen" />}
            />
            <SelectInput
              name="makeCode"
              label={lang.vehicleMake}
              data={makes.map((item) => ({
                id: item.makeCode,
                title: item.makeCode,
                icon: <Icon class="fas-cloud" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.makeCode}
              disabled={!makes.length}
              onChange={({ makeCode }) => setFilters({ ...tempFilters, makeCode })}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="vehicleModelId"
              label={lang.vehicleModel}
              data={models.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-cloud" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.vehicleModelId}
              disabled={!models.length}
              onChange={({ vehicleModelId }) => setFilters({ ...tempFilters, vehicleModelId })}
              icon={<Icon class="fas-cloud" />}
            />
            <TextInput
              name="clientName"
              label={lang.clientName}
              value={tempFilters?.clientName}
              onChange={({ clientName }) => setFilters({ ...tempFilters, clientName })}
              icon={<Icon class="if-quill-pen" />}
            />
            <SelectInput
              name="dealerId"
              label={lang.dealers}
              data={dealers.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-cloud" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.dealerId}
              disabled={!dealers.length}
              onChange={({ dealerId }) => setFilters({ ...tempFilters, dealerId })}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="productId"
              label={lang.products}
              data={products.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-box" />,
              }))}
              isMultiselect
              disableTags={isDesktop}
              value={tempFilters?.productId}
              onChange={({ productId }) => setFilters({ ...tempFilters, productId })}
              icon={<Icon class="fas-box" />}
            />
            <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
              <DateInput
                name="expiryFromDateUtc"
                label={lang.expiryFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.expiryFromDateUtc}
                onChange={({ expiryFromDateUtc }) => setFilters({ ...tempFilters, expiryFromDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="expiryToDateUtc"
                label={lang.expiryToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.expiryToDateUtc}
                onChange={({ expiryToDateUtc }) => setFilters({ ...tempFilters, expiryToDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
            </InputWrapper>
            <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
              <DateInput
                name="effectiveFromDateUtc"
                label={lang.effectiveFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.effectiveFromDateUtc}
                onChange={({ effectiveFromDateUtc }) => setFilters({ ...tempFilters, effectiveFromDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="effectiveToDateUtc"
                label={lang.effectiveToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.effectiveToDateUtc}
                onChange={({ effectiveToDateUtc }) => setFilters({ ...tempFilters, effectiveToDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
            </InputWrapper>
            <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
              <DateInput
                name="createdAtFromDateUtc"
                label={lang.createdAtFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.createdAtFromDateUtc}
                onChange={({ createdAtFromDateUtc }) => setFilters({ ...tempFilters, createdAtFromDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="createdAtToDateUtc"
                label={lang.createdAtToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.createdAtToDateUtc}
                onChange={({ createdAtToDateUtc }) => setFilters({ ...tempFilters, createdAtToDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
            </InputWrapper>
            <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
              <DateInput
                name="modifiedAtFromDateUtc"
                label={lang.modifiedAtFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.modifiedAtFromDateUtc}
                onChange={({ modifiedAtFromDateUtc }) => setFilters({ ...tempFilters, modifiedAtFromDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="modifiedAtToDateUtc"
                label={lang.modifiedAtToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.modifiedAtToDateUtc}
                onChange={({ modifiedAtToDateUtc }) => setFilters({ ...tempFilters, modifiedAtToDateUtc })}
                icon={<Icon class="fas-calendar-alt" />}
              />
            </InputWrapper>
          </>
        );
      }}
    />
  );
}
